import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { StorageService } from "@core/services/utils/storage.service";
import { BEBasicService } from "@services/be-basic.service";
import { Observable, tap } from "rxjs";
import {
  OAuthTokenInfo,
  UserToken,
} from "../../services/security/_models/interfaces/oauth.interfaces";
import {
  LANGUAGE_SESSION_STORE,
  TOKEN_SESSION_STORE,
  USERTOKEN_SESSION_STORE,
} from "../services/utils/constants/constants";

const TOKEN_HEADER_KEY = "Authorization"; // for Node.js Express back-end
const USERTOKEN_HEADER_KEY = "X-LC-TOKEN";
const LANGUAGE_HEADER_KEY = "X-LC-LOCALE";

@Injectable()
export class BEAuthInterceptor implements HttpInterceptor {
  private baseUrl: string;
  private isRedirectingToLogin = false;
  private readonly router = inject(Router);
  private readonly storageService = inject(StorageService);
  private readonly service = inject(BEBasicService);

  constructor() {
    this.baseUrl = this.service.getEnvironmentForServices().apiBaseUrl;
  }

  private redirectToLoginOnce(): void {
    if (
      this.isRedirectingToLogin ||
      this.router.url === "/" ||
      this.router.url.startsWith("/login")
    ) {
      return;
    }

    this.isRedirectingToLogin = true;

    this.router.navigate(["/login"]).finally(() => {
      this.isRedirectingToLogin = false;
    });
  }

  private checkHeaders(
    myBaseURL: string,
    request: HttpRequest<unknown>
  ): boolean {
    if (request != null && myBaseURL != null) {
      if (
        myBaseURL != null &&
        request.url.toLowerCase().includes(myBaseURL.toLowerCase())
      ) {
        return true;
      }
    }
    return false;
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    let authReq = request;
    const stoken = this.storageService.getItem(TOKEN_SESSION_STORE);
    const lang = this.storageService.getItem(LANGUAGE_SESSION_STORE);
    const utoken = this.storageService.getItem(USERTOKEN_SESSION_STORE);
    let authHeader = false;
    let checkedHeaders = false;

    try {
      if (stoken != null) {
        const token: OAuthTokenInfo = JSON.parse(stoken ?? "");
        if (token?.access_token) {
          if (this.checkHeaders(this.baseUrl, request)) {
            checkedHeaders = true;
            if (token != null) {
              authHeader = true;
              authReq = authReq.clone({
                headers: authReq.headers.delete(TOKEN_HEADER_KEY),
              });
              // for Spring Boot back-end
              authReq = authReq.clone({
                headers: authReq.headers.append(
                  TOKEN_HEADER_KEY,
                  "Bearer " + token.access_token
                ),
              });
            }
            if (utoken != null && utoken.length > 0) {
              const utokenobj: UserToken = JSON.parse(utoken);
              if (utokenobj?.token) {
                authReq = authReq.clone({
                  headers: authReq.headers.delete(USERTOKEN_HEADER_KEY),
                });
                authReq = authReq.clone({
                  headers: authReq.headers.append(
                    USERTOKEN_HEADER_KEY,
                    utokenobj.token
                  ),
                });
              }
            }
          }
        }
      }
    } catch {
      // do nothing
    }
    if (lang != null) {
      if (checkedHeaders || this.checkHeaders(this.baseUrl, request)) {
        // for Spring Boot back-end
        authReq = authReq.clone({
          headers: authReq.headers.delete(LANGUAGE_HEADER_KEY),
        });
        authReq = authReq.clone({
          headers: authReq.headers.append(LANGUAGE_HEADER_KEY, lang),
        });
      }
    }

    //Handle the response, that we can also intercept, as needed
    return next.handle(authReq).pipe(
      tap({
        error: (err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status === 401) {
            if (authHeader) {
              this.storageService.removeItem(TOKEN_SESSION_STORE);
              this.storageService.removeItem(USERTOKEN_SESSION_STORE);
            }
            this.redirectToLoginOnce();
          }
        },
      })
    );
  }
}
